/**
 * Workbook model: the in-memory substrate every analysis feature reads
 * from. Built once per scan via the layered ingestion pipeline; cached
 * by fingerprint so repeat scans don't redo work.
 *
 * Layered design (each layer consumes only the previous):
 *   Layer 0 (always): cell classification, numeric extraction, R1C1 interning
 *   Layer 1 (always): region segmentation
 *   Layer 2 (lazy):   formula pattern analysis (call ensurePatternAnalysis)
 *   Layer 3 (lazy):   reference graph + cycle detection (call ensureGraph)
 *
 * Layers 0 and 1 are populated by `getWorkbookModel`. Layers 2 and 3 are
 * `null` until a consumer explicitly asks for them; results are cached on
 * the model so feature A populating Layer 2 means feature B reads it for
 * free.
 *
 * Coordinate convention: per-sheet typed arrays are indexed by flattened
 * (row, col) where row/col are *used-range-relative* — i.e. row 0 is the
 * first row of the sheet's used range. To get absolute sheet coordinates,
 * add `SheetModel.bounds.startRow` / `startCol`. The Layer 3 graph uses
 * *absolute* sheet coordinates because formulas resolve absolutely.
 *
 * See dev_notes/financial-model-review-architecture.md for the full design.
 */

// ============================================================================
// Layer 0 enums
// ============================================================================

/**
 * What put the value in this cell. Stored as Int8Array values per cell;
 * the const object lets call sites write `Origin.FORMULA` while the array
 * holds the integer.
 */
export const Origin = {
  EMPTY: 0,
  CONSTANT: 1,       // user typed/pasted directly
  FORMULA: 2,        // expression in this cell
  SPILL_RESULT: 3,   // value produced by another cell's dynamic array
} as const;
export type OriginValue = (typeof Origin)[keyof typeof Origin];

/**
 * What kind of value is currently there. Reflects the *current* result;
 * for a formula, this is what it evaluated to, not its intrinsic type.
 */
export const ValueKind = {
  NONE: 0,
  NUMBER: 1,
  TEXT: 2,
  DATE: 3,           // numeric value + date format
  BOOLEAN: 4,
  ERROR: 5,          // #REF!, #N/A, #DIV/0!, etc.
} as const;
export type ValueKindValue = (typeof ValueKind)[keyof typeof ValueKind];

// ============================================================================
// Shared geometry
// ============================================================================

/** Bounds of a sheet's used range as returned by Office.js getUsedRange. */
export interface SheetBounds {
  /** 0-based absolute row of the used range's first row. */
  startRow: number;
  /** 0-based absolute column of the used range's first column. */
  startCol: number;
  /** Row count of the used range. */
  rows: number;
  /** Column count of the used range. */
  cols: number;
}

/**
 * A logical block of content (table, list, forecast block, …). Rows and
 * columns are used-range-relative and inclusive on both ends.
 */
export interface Region {
  id: number;
  minRow: number;     // inclusive
  maxRow: number;     // inclusive
  minCol: number;     // inclusive
  maxCol: number;     // inclusive
  /** Bounding-box cell count (rows × cols of the rectangle). */
  area: number;
  /** Non-empty cell count inside the bounding box. */
  cellCount: number;
}

// ============================================================================
// Layer 2: pattern analysis (lazy, per sheet)
// ============================================================================

export type AnomalyType =
  | "hardcode_in_formula_band"
  | "hardcoded_error"
  | "inconsistent_formula";

export interface AnomalyRecord {
  /** Used-range-relative row. */
  row: number;
  /** Used-range-relative column. */
  col: number;
  regionId: number;
  type: AnomalyType;
  /**
   * Severity in [0, 1], higher = more confident this is a real issue.
   * Computed as (dominantGroup.size / region.cellCount) × baseScore[type].
   */
  severityScore: number;
  /** Expected R1C1 pattern id (for inconsistent_formula). */
  expected?: number;
  /** Actual R1C1 pattern id (for inconsistent_formula). */
  actual?: number;
}

/** Layer 2 output, attached per sheet. */
export interface PatternAnalysis {
  /** 1 = flagged. Length = bounds.rows * bounds.cols. */
  anomalyMask: Uint8Array;
  /** Anomalies sorted by severityScore descending. */
  anomalies: AnomalyRecord[];
}

// ============================================================================
// Layer 3: reference graph (lazy, workbook-wide)
// ============================================================================

export const NodeKind = {
  CELL: 0,
  RANGE: 1,
} as const;
export type NodeKindValue = (typeof NodeKind)[keyof typeof NodeKind];

export interface BrokenRef {
  /** Node id of the formula cell whose reference is broken. */
  from: number;
  /** Original target as it appeared in the formula. */
  targetAddr: string;
  reason: string;
}

/**
 * Reference graph in compressed sparse row (CSR) form. Node metadata is
 * stored as parallel typed arrays indexed by node id — no per-node objects.
 *
 * For node n, its out-edges (cells/ranges it points to as a precedent are
 * found in `adjTarget[adjStart[n] : adjStart[n+1]]`. Reverse access (the
 * dependent → precedent direction) is built lazily on first use.
 */
export interface ReferenceGraph {
  // Node metadata — parallel arrays indexed by node id, length = nodeCount
  nodeKind: Int8Array;
  nodeSheet: Int32Array;
  nodeR1: Int32Array;
  nodeC1: Int32Array;
  nodeR2: Int32Array;       // == nodeR1 when nodeKind === CELL
  nodeC2: Int32Array;       // == nodeC1 when nodeKind === CELL

  // Forward CSR (precedent → dependent), built eagerly
  adjStart: Int32Array;     // length = nodeCount + 1
  adjTarget: Int32Array;    // length = edgeCount

  // Reverse CSR (dependent → precedent), built lazily on first traversal
  adjStartIn: Int32Array | null;
  adjTargetIn: Int32Array | null;

  // Derived
  /** SCC id per node from Tarjan. Any SCC with >1 member is a cycle. */
  sccId: Int32Array;
  /** Topological order of the condensed graph. null until requested. */
  topoOrder: Int32Array | null;
  brokenRefs: BrokenRef[];
}

// ============================================================================
// Per-sheet model
// ============================================================================

export interface SheetModel {
  sheetId: number;
  name: string;
  bounds: SheetBounds;

  // ---- Layer 0 (always present after ingestion) ----

  /** Length = bounds.rows * bounds.cols. Values are OriginValue. */
  cellOrigin: Int8Array;
  /** Length = bounds.rows * bounds.cols. Values are ValueKindValue. */
  cellValueKind: Int8Array;
  /** Numeric values, NaN for non-numeric / empty. Length = rows * cols. */
  numericGrid: Float64Array;
  /**
   * R1C1 pattern id per cell, -1 for non-formula. Look up the original
   * string via `catalogs.r1c1Strings[id]`.
   */
  r1c1Ids: Int32Array;
  /** Number format id per cell, -1 for empty. Lookup via `catalogs.numFmtStrings`. */
  numFmtIds: Int32Array;
  /** Count of non-empty cells per row. Length = bounds.rows. */
  rowDensity: Int32Array;
  /** Count of non-empty cells per column. Length = bounds.cols. */
  colDensity: Int32Array;

  /**
   * Text value id per cell, -1 for non-text cells. Look up the original
   * string via `catalogs.textValueStrings[id]`. The dedup ratio for text
   * varies (high on label columns, low on free-form data) — interning
   * costs nothing when unique and pays off when duplicated.
   */
  textValueIds: Int32Array;
  /**
   * A1-form formula id per cell, -1 for non-formula cells. Look up via
   * `catalogs.formulaA1Strings[id]`. Loaded alongside R1C1 because some
   * legacy analyzers (e.g. cell-types usage inference) parse A1 syntax
   * directly and rewriting them for R1C1 would be a significant change.
   */
  formulaA1Ids: Int32Array;

  // ---- Layer 1 (always present after ingestion) ----

  regions: Region[];
  /** Region id per cell, -1 if not in any region. Length = rows * cols. */
  regionOfCell: Int32Array;

  // ---- Layer 2 (lazy — null until ensurePatternAnalysis runs) ----

  patternAnalysis: PatternAnalysis | null;
}

// ============================================================================
// Workbook-level catalogs
// ============================================================================

export interface TableEntry {
  sheetId: number;
  bounds: SheetBounds;
  /** Column name → 0-based column index within the table. */
  columns: Map<string, number>;
}

export interface NameEntry {
  /** null = workbook-scoped, otherwise sheet-scoped (shadows workbook scope). */
  sheetId: number | null;
  bounds: SheetBounds;
}

/**
 * Per-sheet spill index. Given absolute sheet coords `(r, c)`,
 * `contains(r, c)` is true if the cell is inside any spill range on that
 * sheet; `isAnchor(r, c)` is true only for the anchor cell that owns the
 * spill.
 */
export interface SpillSheetIndex {
  contains(r: number, c: number): boolean;
  isAnchor(r: number, c: number): boolean;
}

/**
 * Sheet tab visibility as reported by Office.js, normalized into a
 * string union. Structurally identical to `info/types.ts`'s version —
 * defined here so the model has no reverse dependency on info.
 */
export type SheetVisibility = "visible" | "hidden" | "veryHidden" | "unknown";

export interface SheetMetadata {
  name: string;
  bounds: SheetBounds;
  /** 0-based tab position in the workbook. */
  position: number;
  visibility: SheetVisibility;
}

export interface WorkbookCatalogs {
  /** Sheet name → sheetId. */
  sheetNameDict: Map<string, number>;
  /** sheetId → workbook-level sheet metadata. Reverse of sheetNameDict. */
  sheetsBySheetId: Map<number, SheetMetadata>;

  /** Table name (workbook-global per Excel) → entry. */
  tablesCatalog: Map<string, TableEntry>;
  /** Defined name → entry. Sheet-scoped names shadow workbook-scoped. */
  definedNamesCatalog: Map<string, NameEntry>;
  /** sheetId → per-sheet spill index. */
  spillCatalog: Map<number, SpillSheetIndex>;

  // ---- Workbook-wide string interning ----

  /** R1C1 formula string → id. */
  r1c1Dict: Map<string, number>;
  /** id → R1C1 formula string. `r1c1Strings[id]` reverses `r1c1Dict`. */
  r1c1Strings: string[];
  /** A1 formula string → id. Parallel to r1c1Dict but in A1 form. */
  formulaA1Dict: Map<string, number>;
  /** id → A1 formula string. */
  formulaA1Strings: string[];
  /** Number format string → id. */
  numFmtDict: Map<string, number>;
  /** id → number format string. */
  numFmtStrings: string[];
  /** Text cell value → id. Lookups across sheets share ids. */
  textValueDict: Map<string, number>;
  /** id → text cell value. */
  textValueStrings: string[];
}

// ============================================================================
// Top-level model
// ============================================================================

export type ScanScope = "selection" | "sheet" | "workbook";

export interface WorkbookModel {
  /**
   * Identity tag for cache invalidation. Two models with the same
   * fingerprint and same scope describe the same workbook state. The
   * fingerprint encoding is private to the model layer — consumers only
   * compare for equality.
   */
  fingerprint: string;

  /** Which scope was scanned to produce this model. */
  scope: ScanScope;

  /** Human-readable workbook identifier for logging / display. */
  workbookName: string;

  /** Catalogs are workbook-wide (independent of scope). */
  catalogs: WorkbookCatalogs;

  /**
   * Scanned sheets keyed by sheetId. Sheets with no used range / no
   * content under the active scope are omitted — consumers don't have
   * to handle `bounds.rows === 0`.
   */
  sheets: Map<number, SheetModel>;

  /** Layer 3 — null until ensureGraph runs. */
  graph: ReferenceGraph | null;
}
